// Структура документа: порядок структурных элементов по СТО (вставка на правильное место), диапазон раздела
// (заголовок + содержимое до следующего заголовка того же или более высокого уровня) и перенос разделов.
import type { Editor } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { TextSelection } from '@tiptap/pm/state';
import { focusInside } from '../../core/editor/insert';
import { gotoNode } from '../../core/docIndex';
import { DEFS_INTRO } from '../../core/schema/constants';

/** Место элемента в работе (3.x СТО): нормативные ссылки, определения, введение, [основная часть], выводы, источники, приложения. */
export const STRUCT_RANK: Record<string, number> = {
  normrefs: 1, defs: 2, terms: 2, abbrev: 2, intro: 3, conclusions: 7, conclusion: 7, sources: 8,
};
const BODY_RANK = 5;
const APP_RANK = 9;

function starterRank(n: PMNode): number | null {
  if (n.type.name === 'structHeading') return STRUCT_RANK[n.attrs.kind] ?? BODY_RANK;
  if (n.type.name === 'heading' && Number(n.attrs.level) === 1 && n.textContent.trim()) return BODY_RANK;
  if (n.type.name === 'appendix') return APP_RANK;
  return null;
}

/** Позиция для вставки элемента ранга rank: перед первым «началом раздела» с большим рангом. */
export function positionForRank(doc: PMNode, rank: number): number {
  let pos = -1;
  doc.forEach((n, off) => {
    if (pos >= 0) return;
    const r = starterRank(n);
    if (r !== null && r > rank) pos = off;
  });
  if (pos >= 0) return pos;
  // в конец основной части (перед приложениями - их нет, раз не нашли) - после последнего блока
  return doc.content.size;
}

export function findStruct(doc: PMNode, kinds: string[]): { node: PMNode; pos: number } | null {
  let hit: { node: PMNode; pos: number } | null = null;
  doc.forEach((n, off) => { if (!hit && n.type.name === 'structHeading' && kinds.includes(n.attrs.kind)) hit = { node: n, pos: off }; });
  return hit;
}

/** Добавить структурный элемент на его место по СТО (если уже есть - перейти к нему). */
export function insertStructElement(ed: Editor, kind: string): boolean {
  // eslint-disable-next-line no-param-reassign
  const doc = ed.state.doc;
  if (kind === 'terms' || kind === 'abbrev') kind = 'defs';
  const same = kind === 'conclusions' || kind === 'conclusion' ? ['conclusions', 'conclusion'] : kind === 'defs' ? ['defs', 'terms', 'abbrev'] : [kind];
  const ex = findStruct(doc, same);
  if (ex) { gotoNode(ed, ex.node.attrs.id); return false; }
  const pos = positionForRank(doc, STRUCT_RANK[kind] ?? BODY_RANK);
  const blocks: any[] = [{ type: 'structHeading', attrs: { kind } }];
  // 3.5.2: перечень начинают вводной фразой; дальше - записи «сокращение – расшифровка» (курсор - в первой)
  if (kind === 'defs') blocks.push({ type: 'paragraph', content: [{ type: 'text', text: DEFS_INTRO }] });
  if (kind !== 'sources') blocks.push({ type: 'paragraph' });
  ed.chain().focus().insertContentAt(pos, blocks).run();
  if (kind === 'defs') {
    let at = pos;
    ed.state.doc.nodesBetween(pos, Math.min(ed.state.doc.content.size, pos + 400), (n, p) => { if (n.type.name === 'paragraph' && !n.content.size && at === pos) at = p; return false; });
    focusInside(ed, at);
  } else if (kind !== 'sources') focusInside(ed, pos + 1);
  else ed.view.dispatch(ed.state.tr.setSelection(TextSelection.near(ed.state.doc.resolve(Math.min(pos + 1, ed.state.doc.content.size)))).scrollIntoView());
  return true;
}

// ------------------------------------------------------------------------------------------------ разделы
export interface SectionRange { from: number; to: number; level: number; node: PMNode }

/** Диапазон раздела по id его заголовка (только верхний уровень документа или приложение целиком). */
export function sectionRange(doc: PMNode, id: string): SectionRange | null {
  let start = -1; let level = 0; let startNode: PMNode | null = null;
  let end = -1;
  doc.forEach((n, off) => {
    if (end >= 0) return;
    if (start < 0) {
      if (n.attrs?.id === id) {
        start = off; startNode = n;
        level = n.type.name === 'heading' ? Number(n.attrs.level) : 0;   // 0 - структурный элемент/приложение
        if (n.type.name === 'appendix') end = off + n.nodeSize;
      }
      return;
    }
    const isHead = n.type.name === 'heading' && n.textContent.trim();
    if (n.type.name === 'appendix' || n.type.name === 'structHeading' || (isHead && (level === 0 ? Number(n.attrs.level) === 1 : Number(n.attrs.level) <= level))) end = off;
  });
  if (start < 0 || !startNode) return null;
  return { from: start, to: end >= 0 ? end : doc.content.size, level, node: startNode };
}

/** Перенести раздел id перед (before=true) или после раздела targetId. Одна транзакция - один Ctrl+Z. */
export function moveSection(ed: Editor, id: string, targetId: string, before: boolean): boolean {
  if (id === targetId) return false;
  const doc = ed.state.doc;
  const src = sectionRange(doc, id);
  const dst = sectionRange(doc, targetId);
  if (!src || !dst) return false;
  const at = before ? dst.from : dst.to;
  if (at >= src.from && at <= src.to) return false;          // внутрь самого себя
  const isApp = src.node.type.name === 'appendix';
  const dstApp = dst.node.type.name === 'appendix';
  if (isApp !== dstApp) return false;                         // приложения - только среди приложений (4.16)
  const slice = doc.slice(src.from, src.to);
  const tr = ed.state.tr;
  tr.delete(src.from, src.to);
  const ins = tr.mapping.map(at, before ? -1 : 1);
  try {
    tr.insert(ins, slice.content);
  } catch {
    return false;
  }
  ed.view.dispatch(tr.scrollIntoView());
  return true;
}

/** Повысить/понизить уровень заголовка (1–3). */
export function setHeadingLevel(ed: Editor, id: string, delta: number) {
  ed.state.doc.descendants((n, pos) => {
    if (n.attrs?.id !== id) return true;
    if (n.type.name === 'heading') {
      const l = Math.min(3, Math.max(1, Number(n.attrs.level) + delta));
      ed.view.dispatch(ed.state.tr.setNodeMarkup(pos, undefined, { ...n.attrs, level: l }));
    }
    return false;
  });
}
